/**
 * Dashboard Page - Phase 2 Implementation
 * Main dashboard with connection status, stats, activity timeline, and quick actions
 * Following PRD Phase 2 specifications
 */

import React, { useEffect, useState } from 'react';
import { useConnectionStore } from '../stores/connectionStore';
import { useDashboardStore } from '../stores/dashboardStore';
import { trackComponentMount } from '../utils/debugUtils';
import { useConnectionDebug } from '../hooks/useConnectionDebug';
import { useStrictModeOnce } from '../hooks/useStrictModeOnce';
import { checkAndClearIfNeeded } from '../utils/clearStaleData';
import { DashboardLayout } from '../components/layout';
import ConnectionStatusCard from '../components/dashboard/ConnectionStatusCard';
import MCPHostStatusCard from '../components/dashboard/MCPHostStatusCard';
import QuickStatsGrid from '../components/dashboard/QuickStatsGrid';
import ActivityTimeline from '../components/dashboard/ActivityTimeline';
import QuickActionsPanel from '../components/dashboard/QuickActionsPanel';
import { 
  DashboardStatsGridSkeleton,
  DashboardActivitySkeleton,
  DashboardConnectionCardSkeleton,
  PageHeaderSkeleton
} from '../components/ui/skeleton-patterns';
import { useToast } from '../hooks/use-toast';

const Dashboard: React.FC = () => {
  // Track component mount for debugging and clear stale data
  React.useEffect(() => {
    trackComponentMount('Dashboard');
    // Clear stale connection data on Dashboard mount to ensure fresh state
    checkAndClearIfNeeded();
  }, []);
  
  // Enable debug monitoring for connection API calls
  useConnectionDebug();
  
  const { initializeDashboard } = useConnectionStore();
  const { fetchStats, fetchActivity, fetchConnectionStatus, isLoading: dashboardLoading } = useDashboardStore();
  const { toast } = useToast();
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Initialize dashboard data on mount (StrictMode safe) with retry on failure
  useStrictModeOnce(async () => {
    setIsInitialLoading(true);
    let retryCount = 0;
    const maxRetries = 2;
    
    const initWithRetry = async () => {
      try {
        console.log('🚀 Dashboard initialization started (attempt', retryCount + 1, ')');
        
        // Run initial data fetching in parallel, but use the optimized connection initialization
        const results = await Promise.allSettled([
          initializeDashboard(), // This handles connection check efficiently
          fetchStats(),
          fetchActivity(),
          fetchConnectionStatus()
        ]);
        
        // Check if connection initialization succeeded
        const dashboardInitResult = results[0];
        if (dashboardInitResult.status === 'rejected' && retryCount < maxRetries) {
          retryCount++;
          console.log(`⚠️ Dashboard init failed, retrying (${retryCount}/${maxRetries})...`);
          
          // Wait before retry with exponential backoff
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retryCount)));
          
          // Retry the initialization
          return initWithRetry();
        }
        
        // If connected but no companies, trigger a refresh
        const state = useConnectionStore.getState();
        if (state.status === 'connected' && state.companies.length === 0) {
          setTimeout(async () => {
            try {
              await state.refreshWithCompanies();
              toast({
                title: "Companies Loaded",
                description: `Found ${useConnectionStore.getState().companies.length} companies from Tally`,
              });
            } catch (err) {
              console.warn('Auto-refresh companies failed:', err);
              // Don't show error toast for auto-refresh failure
            }
          }, 2000);
        }
        
      } catch (error) {
        console.error('Dashboard initialization error:', error);
        
        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`⚠️ Dashboard init error, retrying (${retryCount}/${maxRetries})...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retryCount)));
          return initWithRetry();
        }
        
        toast({
          title: "Dashboard Error",
          description: "Could not connect to Tally. Please check if Tally and TallyConnector are running.",
          variant: "destructive",
        });
      }
    };
    
    await initWithRetry();
    
    // Add minimum loading time for better UX
    setTimeout(() => {
      setIsInitialLoading(false);
    }, 800);
  }, [initializeDashboard, fetchStats, fetchActivity, fetchConnectionStatus, toast]);

  if (isInitialLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <PageHeaderSkeleton />
          
          {/* Connection Status Skeleton */}
          <div className="grid gap-6">
            <DashboardConnectionCardSkeleton />
          </div>

          {/* Main Dashboard Grid Skeleton */}
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <DashboardStatsGridSkeleton />
              <DashboardActivitySkeleton />
            </div>
            <div className="lg:col-span-1">
              <DashboardConnectionCardSkeleton />
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="mb-6">
          <div>
            <p className="text-muted-foreground">
              Monitor your Tally connection, view financial metrics, and manage your accounting data.
            </p>
          </div>
        </div>

      {/* Connection Status Cards - Side by side */}
      <div className="grid gap-6 lg:grid-cols-2">
        <ConnectionStatusCard />
        <MCPHostStatusCard />
      </div>

      {/* Main Dashboard Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Stats & Activity */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quick Stats Grid */}
          <QuickStatsGrid />
          
          {/* Recent Activity Timeline */}
          <ActivityTimeline maxItems={8} />
        </div>

        {/* Right Column - Quick Actions */}
        <div className="lg:col-span-1">
          <QuickActionsPanel />
        </div>
      </div>
    </div>
    </DashboardLayout>
  );
};

export default Dashboard;